import { ApiRequestError, apiRequest, apiUpload } from '../api';
import { ensureRolesLoaded, renderUserBox } from '../app';
import { invalidatePublic } from '../data';
import { formatDate, t, type TranslationKey } from '../i18n';
import { clearSession, saveSession, state } from '../state';
import {
  MEDIA_TYPES,
  NEWS_CATEGORIES,
  PLAYER_ROLES,
  RACES,
  type Application,
  type ApplicationStatus,
  type Roster,
  type SessionUser,
} from '../types';
import { $, emptyState, errorState, h, link, loadingState, svg } from '../ui/dom';
import { icons } from '../ui/icons';
import { toast } from '../ui/overlay';

// Field labels are Russian-only on purpose: the admin panel is used by clan staff.

type Row = Record<string, unknown> & { id: number };
type Option = { value: string; label: string };
type FieldType = 'text' | 'textarea' | 'url' | 'image' | 'date' | 'datetime' | 'number' | 'select' | 'bool' | 'gallery' | 'links';
type FieldDef = {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  wide?: boolean;
  hint?: string;
  options?: () => Option[];
};
type EntityKey = 'news' | 'achievements' | 'media' | 'rosters' | 'players';
type EntityDef = {
  key: EntityKey;
  endpoint: string;
  fields: FieldDef[];
  title: (r: Row) => string;
  subtitle: (r: Row) => string;
  href?: (r: Row) => string;
  defaults: () => Record<string, unknown>;
};
type TabKey = EntityKey | 'applications';

const TABS: TabKey[] = ['news', 'achievements', 'media', 'rosters', 'players', 'applications'];
const APP_STATUSES: ApplicationStatus[] = ['new', 'accepted', 'rejected', 'archived'];

let rosterOptions: Option[] = [];
let activeTab: TabKey = 'news';
let controller: AbortController | null = null;

const opt = (values: readonly string[], prefix: string): (() => Option[]) => () =>
  values.map((v) => ({ value: v, label: t(`${prefix}.${v}` as TranslationKey) }));

const today = (): string => new Date().toISOString().slice(0, 10);

const ENTITIES: Record<EntityKey, EntityDef> = {
  news: {
    key: 'news',
    endpoint: '/api/news',
    title: (r) => String(r.title_ru ?? ''),
    subtitle: (r) =>
      `${t(`news.cat.${String(r.category)}` as TranslationKey)} · ${formatDate(String(r.published_at), true)}${r.is_published ? '' : ` · ${t('admin.draft')}`}`,
    href: (r) => `/news?id=${r.id}`,
    defaults: () => ({ category: 'team', published_at: new Date().toISOString(), is_published: true }),
    fields: [
      { name: 'title_ru', label: 'Заголовок (RU)', type: 'text', required: true },
      { name: 'title_en', label: 'Заголовок (EN)', type: 'text' },
      { name: 'category', label: 'Категория', type: 'select', required: true, options: opt(NEWS_CATEGORIES, 'news.cat') },
      { name: 'published_at', label: 'Дата публикации', type: 'datetime', required: true, hint: 'Можно поставить в будущее — новость появится в этот момент' },
      { name: 'cover_url', label: 'Обложка', type: 'image', wide: true },
      { name: 'summary_ru', label: 'Краткое описание (RU)', type: 'textarea' },
      { name: 'summary_en', label: 'Краткое описание (EN)', type: 'textarea' },
      { name: 'body_ru', label: 'Полный текст (RU)', type: 'textarea', wide: true, hint: 'Пустая строка — новый абзац, **жирный**, ссылки вставляются как есть' },
      { name: 'body_en', label: 'Полный текст (EN)', type: 'textarea', wide: true },
      { name: 'video_url', label: 'Видео (YouTube / Twitch)', type: 'url', wide: true },
      { name: 'gallery', label: 'Галерея', type: 'gallery', wide: true, hint: 'По одной ссылке на строку или загрузите файлы' },
      { name: 'links', label: 'Внешние ссылки', type: 'links', wide: true, hint: 'Формат строки: Название | https://…' },
      { name: 'is_published', label: 'Опубликовано', type: 'bool' },
    ],
  },
  achievements: {
    key: 'achievements',
    endpoint: '/api/achievements',
    title: (r) => String(r.title_ru ?? ''),
    subtitle: (r) => `${formatDate(String(r.event_date))} · ${String(r.place ?? '')} · ${String(r.mmr ?? '—')}`,
    href: () => '/achievements',
    defaults: () => ({ event_date: today() }),
    fields: [
      { name: 'title_ru', label: 'Название события (RU)', type: 'text', required: true },
      { name: 'title_en', label: 'Название события (EN)', type: 'text' },
      { name: 'event_date', label: 'Дата', type: 'date', required: true },
      { name: 'place', label: 'Итоговое место', type: 'text', required: true, hint: 'Например: 1, 2, Top-4, 5–8' },
      { name: 'mmr', label: 'MMR', type: 'text', hint: 'Например: 4400–4900 или gAz C' },
      { name: 'source_url', label: 'Ссылка на результаты', type: 'url' },
      { name: 'vod_url', label: 'Запись события', type: 'url' },
      { name: 'note_ru', label: 'Комментарий (RU)', type: 'textarea' },
      { name: 'note_en', label: 'Комментарий (EN)', type: 'textarea' },
    ],
  },
  media: {
    key: 'media',
    endpoint: '/api/media',
    title: (r) => String(r.title_ru ?? ''),
    subtitle: (r) => `${t(`media.type.${String(r.media_type)}` as TranslationKey)} · ${formatDate(String(r.published_at))}`,
    href: () => '/media',
    defaults: () => ({ media_type: 'match', published_at: today() }),
    fields: [
      { name: 'title_ru', label: 'Название (RU)', type: 'text', required: true },
      { name: 'title_en', label: 'Название (EN)', type: 'text' },
      { name: 'media_type', label: 'Тип', type: 'select', required: true, options: opt(MEDIA_TYPES, 'media.type') },
      { name: 'published_at', label: 'Дата', type: 'date', required: true },
      { name: 'url', label: 'Ссылка / файл', type: 'image', required: true, wide: true, hint: 'Видео: ссылка на YouTube/Twitch. Скриншот/фото: загрузите файл' },
      { name: 'preview_url', label: 'Превью', type: 'image', wide: true, hint: 'Необязательно: для YouTube превью подставится автоматически' },
    ],
  },
  rosters: {
    key: 'rosters',
    endpoint: '/api/rosters',
    title: (r) => String(r.name ?? ''),
    subtitle: (r) =>
      `MMR ${r.mmr_min ?? '…'}–${r.mmr_max ?? '…'} · ${Array.isArray(r.players) ? r.players.length : 0} игроков`,
    href: (r) => `/roster#roster-${r.id}`,
    defaults: () => ({ sort: 0 }),
    fields: [
      { name: 'name', label: 'Название', type: 'text', required: true },
      { name: 'sort', label: 'Порядок', type: 'number', hint: 'Меньше — выше в списке' },
      { name: 'mmr_min', label: 'MMR от', type: 'number' },
      { name: 'mmr_max', label: 'MMR до', type: 'number' },
      { name: 'description_ru', label: 'Описание (RU)', type: 'textarea' },
      { name: 'description_en', label: 'Описание (EN)', type: 'textarea' },
    ],
  },
  players: {
    key: 'players',
    endpoint: '/api/players',
    title: (r) => String(r.nickname ?? ''),
    subtitle: (r) => {
      const roster = r.roster as { name?: string } | null;
      return `${t(`race.${String(r.race)}` as TranslationKey)} · ${t(`role.${String(r.role)}` as TranslationKey)} · ${roster?.name ?? '—'} · ${r.mmr ?? '—'} MMR`;
    },
    href: (r) => `/player?id=${r.id}`,
    defaults: () => ({ race: 'protoss', role: 'member', sort: 0 }),
    fields: [
      { name: 'nickname', label: 'Ник', type: 'text', required: true },
      { name: 'roster_id', label: 'Состав', type: 'select', options: () => [{ value: '', label: t('admin.noRoster') }, ...rosterOptions] },
      { name: 'race', label: 'Раса', type: 'select', required: true, options: opt(RACES, 'race') },
      { name: 'role', label: 'Роль', type: 'select', required: true, options: opt(PLAYER_ROLES, 'role') },
      { name: 'mmr', label: 'MMR', type: 'number' },
      { name: 'pulse_url', label: 'SC2: Pulse — ссылка', type: 'url' },
      { name: 'battle_tag', label: 'BattleTag', type: 'text' },
      { name: 'country', label: 'Страна', type: 'text' },
      { name: 'twitch_url', label: 'Twitch', type: 'url', hint: 'Нужен для плашки «В эфире»' },
      { name: 'youtube_url', label: 'YouTube', type: 'url' },
      { name: 'avatar_url', label: 'Аватар', type: 'image', wide: true },
      { name: 'bio_ru', label: 'О игроке (RU)', type: 'textarea' },
      { name: 'bio_en', label: 'О игроке (EN)', type: 'textarea' },
      { name: 'sort', label: 'Порядок', type: 'number' },
    ],
  },
};

// ───────────────────────── auth ─────────────────────────

function renderLogin(root: HTMLElement, message?: string): void {
  const err = h('p', { class: `form-error${message ? '' : ' hidden'}`, text: message ?? '', attrs: { role: 'alert' } });
  const email = h('input', { attrs: { type: 'email', name: 'email', required: true, autocomplete: 'username' } });
  const password = h('input', { attrs: { type: 'password', name: 'password', required: true, autocomplete: 'current-password' } });
  const btn = h('button', { class: 'btn btn-primary btn-wide', attrs: { type: 'submit' }, text: t('admin.signIn') });

  const form = h(
    'form',
    { class: 'form panel admin-login', attrs: { novalidate: true } },
    h('div', 'admin-login-head', svg(icons.admin), h('span', 'mono', 'ROOT ACCESS')),
    h('label', 'field', h('span', { class: 'field-label', text: t('admin.email') }), email),
    h('label', 'field', h('span', { class: 'field-label', text: t('admin.password') }), password),
    err,
    btn,
  );
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    btn.disabled = true;
    btn.textContent = t('admin.signingIn');
    err.classList.add('hidden');
    try {
      const data = await apiRequest<{
        session: { token: string; refresh_token: string; expires_at: number | null };
        user: SessionUser;
      }>('/api/auth/login', { method: 'POST', body: { email: email.value, password: password.value } });
      saveSession({ ...data.session, user: data.user });
      await ensureRolesLoaded(true);
      renderUserBox();
      void renderAdmin();
    } catch (e2) {
      err.textContent =
        e2 instanceof ApiRequestError && e2.status === 429 ? t('join.rateLimited') : t('admin.badCredentials');
      err.classList.remove('hidden');
    } finally {
      btn.disabled = false;
      btn.textContent = t('admin.signIn');
    }
  });
  root.replaceChildren(form);
  email.focus();
}

async function logout(): Promise<void> {
  try {
    await apiRequest('/api/auth/logout', { method: 'POST', auth: true });
  } catch {
    /* ignore */
  }
  clearSession();
  renderUserBox();
  void renderAdmin();
}

// ───────────────────────── form ─────────────────────────

function toLocalInput(iso: unknown): string {
  if (typeof iso !== 'string' || !iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

type Getter = () => unknown;

function uploadButton(onUrl: (url: string) => void, multiple = false): HTMLElement {
  const input = h('input', { attrs: { type: 'file', accept: 'image/*', hidden: true, multiple } });
  const btn = h('button', { class: 'btn btn-ghost btn-sm', attrs: { type: 'button' } }, svg(icons.upload), t('admin.upload'));
  btn.addEventListener('click', () => input.click());
  input.addEventListener('change', async () => {
    const files = Array.from(input.files ?? []);
    if (!files.length) return;
    btn.setAttribute('disabled', '');
    btn.lastChild!.textContent = t('admin.uploading');
    try {
      for (const f of files) onUrl(await apiUpload(f));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Upload failed', 'error');
    } finally {
      btn.removeAttribute('disabled');
      btn.lastChild!.textContent = t('admin.upload');
      input.value = '';
    }
  });
  return h('span', 'upload', btn, input);
}

function buildField(f: FieldDef, value: unknown): { el: HTMLElement; get: Getter } {
  const label = h('span', 'field-label', f.label, f.required ? h('i', null, ' *') : null);
  const hint = f.hint ? h('small', { text: f.hint }) : null;
  const cls = `field${f.wide || f.type === 'textarea' ? ' field-wide' : ''}`;
  const str = value === null || value === undefined ? '' : String(value);

  switch (f.type) {
    case 'textarea': {
      const ta = h('textarea', { attrs: { name: f.name, rows: f.name.startsWith('body') ? 10 : 3 } });
      ta.value = str;
      return { el: h('label', cls, label, ta, hint), get: () => ta.value };
    }
    case 'select': {
      const sel = h('select', { attrs: { name: f.name } }, ...(f.options?.() ?? []).map((o) => h('option', { attrs: { value: o.value }, text: o.label })));
      sel.value = str;
      return { el: h('label', cls, label, sel, hint), get: () => sel.value };
    }
    case 'bool': {
      const cb = h('input', { attrs: { type: 'checkbox', name: f.name } });
      cb.checked = value === true;
      return { el: h('label', 'field field-check', cb, h('span', { text: f.label })), get: () => cb.checked };
    }
    case 'datetime': {
      const inp = h('input', { attrs: { type: 'datetime-local', name: f.name } });
      inp.value = toLocalInput(value);
      return { el: h('label', cls, label, inp, hint), get: () => (inp.value ? new Date(inp.value).toISOString() : '') };
    }
    case 'date':
    case 'number':
    case 'url':
    case 'text': {
      const type = f.type === 'text' ? 'text' : f.type;
      const inp = h('input', { attrs: { type, name: f.name } });
      inp.value = str;
      return { el: h('label', cls, label, inp, hint), get: () => inp.value };
    }
    case 'image': {
      const inp = h('input', { attrs: { type: 'url', name: f.name, placeholder: 'https://…' } });
      inp.value = str;
      const preview = h('img', { class: 'img-preview', attrs: { alt: '' } });
      const sync = (): void => {
        preview.src = inp.value;
        preview.classList.toggle('hidden', !inp.value);
      };
      inp.addEventListener('input', sync);
      sync();
      const up = uploadButton((url) => {
        inp.value = url;
        sync();
      });
      return { el: h('div', cls, label, h('div', 'img-row', inp, up), hint, preview), get: () => inp.value };
    }
    case 'gallery': {
      const ta = h('textarea', { attrs: { name: f.name, rows: 4, placeholder: 'https://…' } });
      ta.value = Array.isArray(value) ? (value as string[]).join('\n') : '';
      const up = uploadButton((url) => {
        ta.value = ta.value.trim() ? `${ta.value.trim()}\n${url}` : url;
      }, true);
      return {
        el: h('div', cls, label, ta, h('div', 'img-row', up), hint),
        get: () => ta.value.split('\n').map((s) => s.trim()).filter(Boolean),
      };
    }
    case 'links': {
      const ta = h('textarea', { attrs: { name: f.name, rows: 3, placeholder: 'Сетка турнира | https://challonge.com/…' } });
      ta.value = Array.isArray(value)
        ? (value as { label: string; url: string }[]).map((l) => `${l.label} | ${l.url}`).join('\n')
        : '';
      return {
        el: h('label', cls, label, ta, hint),
        get: () =>
          ta.value
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line) => {
              const i = line.lastIndexOf('|');
              return i === -1 ? { label: '', url: line } : { label: line.slice(0, i).trim(), url: line.slice(i + 1).trim() };
            }),
      };
    }
  }
}

function renderForm(panel: HTMLElement, def: EntityDef, row: Row | null, onDone: () => void): void {
  const values: Record<string, unknown> = row ?? def.defaults();
  const getters = new Map<string, Getter>();
  const fields = def.fields.map((f) => {
    const { el, get } = buildField(f, values[f.name]);
    getters.set(f.name, get);
    return el;
  });
  const err = h('p', { class: 'form-error hidden', attrs: { role: 'alert' } });
  const save = h('button', { class: 'btn btn-primary', attrs: { type: 'submit' }, text: t('admin.save') });
  const cancel = h('button', { class: 'btn btn-ghost', attrs: { type: 'button' }, text: t('admin.cancel'), on: { click: onDone } });

  const form = h(
    'form',
    { class: 'form panel admin-form', attrs: { novalidate: true } },
    h('div', 'admin-form-head', h('span', 'mono accent', row ? `// EDIT #${row.id}` : '// NEW'), h('h2', { text: row ? def.title(row) : t('admin.new') })),
    h('div', 'form-grid', ...fields),
    err,
    h('div', 'form-actions', save, cancel),
  );

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    err.classList.add('hidden');
    const body: Record<string, unknown> = {};
    for (const [name, get] of getters) body[name] = get();
    save.disabled = true;
    try {
      await apiRequest(row ? `${def.endpoint}/${row.id}` : def.endpoint, {
        method: row ? 'PATCH' : 'POST',
        body,
        auth: true,
      });
      invalidatePublic();
      toast(t('admin.saved'));
      onDone();
    } catch (e2) {
      err.textContent = e2 instanceof Error ? e2.message : String(e2);
      err.classList.remove('hidden');
    } finally {
      save.disabled = false;
    }
  });

  panel.replaceChildren(form);
  form.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// ───────────────────────── entity list ─────────────────────────

async function loadRosterOptions(): Promise<void> {
  const { items } = await apiRequest<{ items: Roster[] }>('/api/rosters?all=1', { auth: true });
  rosterOptions = items.map((r) => ({ value: String(r.id), label: r.name }));
}

async function renderEntity(panel: HTMLElement, def: EntityDef): Promise<void> {
  panel.replaceChildren(loadingState());
  let rows: Row[];
  try {
    if (def.key === 'players') await loadRosterOptions();
    rows = (await apiRequest<{ items: Row[] }>(`${def.endpoint}?all=1`, { auth: true })).items;
  } catch (e) {
    if (e instanceof ApiRequestError && (e.status === 401 || e.status === 403)) return void renderAdmin();
    panel.replaceChildren(errorState(() => void renderEntity(panel, def)));
    return;
  }

  const reload = (): void => void renderEntity(panel, def);
  const addBtn = h('button', { class: 'btn btn-primary btn-sm', attrs: { type: 'button' } }, '+ ', t('admin.new'));
  addBtn.addEventListener('click', () => renderForm(panel, def, null, reload));

  const list = rows.length
    ? h(
        'div',
        'admin-list',
        ...rows.map((r) => {
          const edit = h('button', { class: 'btn btn-ghost btn-sm', attrs: { type: 'button' }, text: t('admin.edit') });
          edit.addEventListener('click', () => renderForm(panel, def, r, reload));
          const del = h('button', { class: 'btn btn-danger btn-sm', attrs: { type: 'button' }, text: t('admin.delete') });
          del.addEventListener('click', async () => {
            if (!confirm(t('admin.confirmDelete', { name: def.title(r) }))) return;
            try {
              await apiRequest(`${def.endpoint}/${r.id}`, { method: 'DELETE', auth: true });
              invalidatePublic();
              toast(t('admin.deleted'));
              reload();
            } catch (e) {
              toast(e instanceof Error ? e.message : 'Error', 'error');
            }
          });
          return h(
            'div',
            'admin-row',
            h('span', 'mono admin-id', `#${r.id}`),
            h('div', 'admin-row-main', h('b', { text: def.title(r) }), h('small', { class: 'mono', text: def.subtitle(r) })),
            h(
              'div',
              'admin-row-actions',
              def.href ? link(def.href(r), { class: 'icon-btn', attrs: { title: t('common.open'), target: '_blank' } }, svg(icons.external)) : null,
              edit,
              del,
            ),
          );
        }),
      )
    : emptyState();

  panel.replaceChildren(h('div', 'admin-toolbar', h('span', 'mono', `${rows.length} ${t('common.records')}`), addBtn), list);
}

// ───────────────────────── applications ─────────────────────────

async function renderApplications(panel: HTMLElement): Promise<void> {
  panel.replaceChildren(loadingState());
  let items: Application[];
  try {
    items = (await apiRequest<{ items: Application[] }>('/api/applications', { auth: true })).items;
  } catch {
    panel.replaceChildren(errorState(() => void renderApplications(panel)));
    return;
  }
  if (!items.length) {
    panel.replaceChildren(emptyState());
    return;
  }

  panel.replaceChildren(
    h(
      'div',
      'apps',
      ...items.map((a) => {
        const sel = h(
          'select',
          { class: `status status-${a.status}` },
          ...APP_STATUSES.map((s) => h('option', { attrs: { value: s }, text: t(`admin.status.${s}` as TranslationKey) })),
        );
        sel.value = a.status;
        sel.addEventListener('change', async () => {
          try {
            await apiRequest(`/api/applications/${a.id}`, { method: 'PATCH', auth: true, body: { status: sel.value } });
            sel.className = `status status-${sel.value}`;
            toast(t('admin.saved'));
          } catch (e) {
            sel.value = a.status;
            toast(e instanceof Error ? e.message : 'Error', 'error');
          }
        });
        return h(
          'article',
          `app-card panel status-${a.status}`,
          h('header', 'app-head', h('b', { text: a.nickname }), h('span', { class: 'mono', text: `${a.mmr} MMR` }), h('time', { class: 'mono muted', text: formatDate(a.created_at, true) }), sel),
          h(
            'dl',
            'app-facts',
            h('div', null, h('dt', { text: t('join.profile') }), h('dd', null, link(a.profile_url, 'inline-link', a.profile_url))),
            h('div', null, h('dt', { text: t('join.timezone') }), h('dd', { text: a.timezone })),
            h('div', null, h('dt', { text: t('join.contact') }), h('dd', { text: a.contact })),
          ),
          a.comment ? h('p', { class: 'app-comment', text: a.comment }) : null,
        );
      }),
    ),
  );
}

// ───────────────────────── shell ─────────────────────────

async function renderAdmin(): Promise<void> {
  const root = $('admin-root');
  const who = $('admin-user');
  if (!root) return;
  if (who) who.textContent = state.user?.email ?? '';

  if (!state.token) {
    renderLogin(root);
    return;
  }
  root.replaceChildren(loadingState());
  await ensureRolesLoaded(true);
  if (!state.token) return renderLogin(root);
  if (!state.user?.isAdmin) {
    const out = h('button', { class: 'btn btn-ghost btn-sm', attrs: { type: 'button' }, text: t('admin.logout') });
    out.addEventListener('click', () => void logout());
    root.replaceChildren(h('div', 'state state-error', h('span', { text: t('admin.notAdmin') }), out));
    return;
  }

  const panel = h('div', 'admin-panel');
  const tabs = h(
    'div',
    'admin-tabs chips',
    ...TABS.map((tab) => {
      const b = h('button', {
        class: `chip${tab === activeTab ? ' active' : ''}`,
        attrs: { type: 'button' },
        text: t(`admin.tab.${tab}` as TranslationKey),
      });
      b.addEventListener('click', () => {
        activeTab = tab;
        tabs.querySelectorAll('.chip').forEach((c) => c.classList.toggle('active', c === b));
        history.replaceState(history.state, '', `/admin?tab=${tab}`);
        showTab();
      });
      return b;
    }),
  );
  const out = h('button', { class: 'btn btn-ghost btn-sm', attrs: { type: 'button' }, text: t('admin.logout') });
  out.addEventListener('click', () => void logout());

  const showTab = (): void => {
    if (activeTab === 'applications') void renderApplications(panel);
    else void renderEntity(panel, ENTITIES[activeTab]);
  };

  root.replaceChildren(h('div', 'admin-bar', tabs, out), panel);
  showTab();
}

export function mountAdmin(params: URLSearchParams): void {
  controller?.abort();
  controller = new AbortController();
  const tab = params.get('tab');
  if (tab && (TABS as string[]).includes(tab)) activeTab = tab as TabKey;
  void renderAdmin();
}

export function unmountAdmin(): void {
  controller?.abort();
  controller = null;
}
